import { IPublicClientApplication } from '@azure/msal-browser';
import { getDataverseHeaders } from '../auth/authService';
import { getDataverseApiBase } from '../services/environmentService';

interface ListOptions {
  select?: string[];
  filter?: string;
  orderby?: string;
  top?: number;
  expand?: string;
}

function buildQuery(options: ListOptions): string {
  const params: string[] = [];
  if (options.select?.length) params.push(`$select=${options.select.join(',')}`);
  if (options.filter)         params.push(`$filter=${encodeURIComponent(options.filter)}`);
  if (options.orderby)        params.push(`$orderby=${options.orderby}`);
  if (options.top)            params.push(`$top=${options.top}`);
  if (options.expand)         params.push(`$expand=${options.expand}`);
  return params.length ? `?${params.join('&')}` : '';
}

export async function dvList<T>(
  pca: IPublicClientApplication,
  entitySetName: string,
  options: ListOptions = {}
): Promise<T[]> {
  const headers = await getDataverseHeaders(pca);
  const res = await fetch(`${getDataverseApiBase()}/${entitySetName}${buildQuery(options)}`, { headers });
  if (!res.ok) throw new Error(`dvList ${entitySetName} failed: ${res.status}`);
  return (await res.json()).value as T[];
}

export async function dvGet<T>(
  pca: IPublicClientApplication,
  entitySetName: string,
  id: string,
  options: Pick<ListOptions, 'select' | 'expand'> = {}
): Promise<T> {
  const headers = await getDataverseHeaders(pca);
  const res = await fetch(`${getDataverseApiBase()}/${entitySetName}(${id})${buildQuery(options)}`, { headers });
  if (!res.ok) throw new Error(`dvGet ${entitySetName}(${id}) failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function dvCreate(
  pca: IPublicClientApplication,
  entitySetName: string,
  data: Record<string, unknown>
): Promise<string> {
  const headers = await getDataverseHeaders(pca);
  const res = await fetch(`${getDataverseApiBase()}/${entitySetName}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`dvCreate ${entitySetName} failed: ${res.status}`);
  // Dataverse returns the new record URL in the OData-EntityId header:
  //   https://{env}/api/data/v9.2/{entitySet}(<guid>)
  const entityIdHeader = res.headers.get('OData-EntityId') ?? res.headers.get('odata-entityid') ?? '';
  const match = entityIdHeader.match(/\(([0-9a-f-]{36})\)/i);
  if (!match) throw new Error(`dvCreate ${entitySetName} succeeded but OData-EntityId header missing or malformed`);
  return match[1];
}

export async function dvUpdate(
  pca: IPublicClientApplication,
  entitySetName: string,
  id: string,
  data: Record<string, unknown>
): Promise<void> {
  const headers = await getDataverseHeaders(pca);
  const res = await fetch(`${getDataverseApiBase()}/${entitySetName}(${id})`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`dvUpdate ${entitySetName}(${id}) failed: ${res.status}`);
}

export async function dvDelete(
  pca: IPublicClientApplication,
  entitySetName: string,
  id: string
): Promise<void> {
  const headers = await getDataverseHeaders(pca);
  const res = await fetch(`${getDataverseApiBase()}/${entitySetName}(${id})`, { method: 'DELETE', headers });
  if (!res.ok) throw new Error(`dvDelete ${entitySetName}(${id}) failed: ${res.status}`);
}

// Clears a single-valued lookup. PATCHing a lookup to null isn't reliable in
// Dataverse — the documented way is DELETE on the navigation property $ref.
export async function dvClearLookup(
  pca: IPublicClientApplication,
  entitySetName: string,
  id: string,
  navProperty: string
): Promise<void> {
  const headers = await getDataverseHeaders(pca);
  const res = await fetch(`${getDataverseApiBase()}/${entitySetName}(${id})/${navProperty}/$ref`, { method: 'DELETE', headers });
  if (!res.ok) throw new Error(`dvClearLookup ${entitySetName}(${id}).${navProperty} failed: ${res.status}`);
}
